import { Box, BoxProps, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import { useMemo } from 'react'
import {
  Cell,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'

import { FloatingChartTooltipShell } from '../chartTooltip/ChartTooltipPointerContext'
import { FloatingPieChartTooltip } from '../chartTooltip/FloatingPieChartTooltip'
import '../common/Chart.css'
import {
  PIE_CHART_DIAMETER,
  PIE_CHART_MAX_LEGEND_GAP,
  PIE_CHART_MIN_LEGEND_GAP,
  PIE_OTHER_THRESHOLD_PCT, // getPieChartColor,
  getPieChartColor,
} from '../common/chart.constants'
import { ChartData } from '../common/chart.types'
import { formatChartDataLabelValue } from '../common/chartFormat'

export type PieChartProps = BoxProps & {
  chartData: ChartData
  /** Override chart height (px). */
  chartHeight?: number
  /** Minimum percent for a slice to display individually. Below this they become "Other". */
  otherThresholdPct?: number
  /** Label under the center total (e.g. cube context). Default matches Port-to-Port "Container Count". */
  centerTotalCaption?: string
  /** Row label for the slice value in the floating tooltip (e.g. "Container Count"). */
  tooltipValueLabel?: string
}

const RADIAN = Math.PI / 180
const LABEL_OFFSET = 12
/** Donut geometry — keep in sync with `<Pie innerRadius` / `outerRadius` below. */
const PIE_INNER_RADIUS = 90
const PIE_OUTER_RADIUS = 130
/**
 * Width of the SVG column only (not the legend). If this grows with `flex="1"`, Recharts
 * centers the donut in the full width and the legend reads as "miles away"; match Figma by
 * sizing the plot to the donut + slice % labels.
 */
const PIE_PLOT_WIDTH_PX = (PIE_OUTER_RADIUS + LABEL_OFFSET + 28) * 2
const DEFAULT_VALUE_KEY = 'value'
const DEFAULT_NAME_KEY = 'key'

export const PieChart = ({
  chartData,
  chartHeight = PIE_CHART_DIAMETER,
  otherThresholdPct = PIE_OTHER_THRESHOLD_PCT,
  centerTotalCaption = 'Total Containers',
  tooltipValueLabel,
  ...props
}: PieChartProps) => {
  const valueKey = chartData.valueKey ?? DEFAULT_VALUE_KEY
  const nameKey = chartData.nameKey ?? DEFAULT_NAME_KEY
  const values = chartData.chartValues ?? []

  const total = useMemo(
    () =>
      values.reduce((sum, entry) => {
        const val = entry[valueKey]
        return sum + (Number(val) || 0)
      }, 0),
    [values, valueKey],
  )

  const { visible, other } = useMemo(() => {
    const vis: typeof values = []
    let otherTotal = 0
    const otherItems: { name: string; value: number }[] = []
    for (const entry of values) {
      const rawVal = entry[valueKey]
      const rawName = entry[nameKey]
      const val = Number(rawVal) || 0
      const pct = total > 0 ? (val / total) * 100 : 0
      if (pct >= otherThresholdPct) {
        vis.push(entry)
      } else {
        otherTotal += val
        otherItems.push({ name: String(rawName ?? ''), value: val })
      }
    }
    otherItems.sort((a, b) => b.value - a.value)
    return {
      visible: vis,
      other:
        otherTotal > 0
          ? {
              [nameKey]: 'Other',
              [valueKey]: otherTotal,
              _otherItems: otherItems,
              _isOther: true,
            }
          : null,
    }
  }, [values, valueKey, nameKey, total, otherThresholdPct])

  const chartSlices = useMemo(
    () => (other ? [...visible, other] : visible),
    [visible, other],
  )

  const dataWithColors = chartSlices.map((entry, index) => ({
    ...entry,
    fill: getPieChartColor(index),
  }))

  const renderPercentLabel = (labelProps: {
    cx: number
    cy: number
    midAngle: number
    outerRadius: number
    value?: number
  }) => {
    const { cx, cy, midAngle, outerRadius, value } = labelProps
    const pct = total > 0 ? Math.round(((value ?? 0) / total) * 100) : 0
    if (pct < 1) return null
    const radius = outerRadius + LABEL_OFFSET
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)
    const textAnchor = x >= cx ? 'start' : 'end'
    const dx = x >= cx ? 6 : -6
    return (
      <text
        x={x}
        y={y}
        dx={dx}
        textAnchor={textAnchor}
        dominantBaseline="central"
        fill="white"
        style={{ fontSize: 12, fontWeight: 700 }}
      >
        {pct}%
      </text>
    )
  }

  const FloatingPieTooltipWrapper = (tooltipProps: any) => (
    <FloatingPieChartTooltip
      {...tooltipProps}
      total={total}
      nameKey={nameKey}
      valueFormat={chartData.valueFormat}
      valueLabel={tooltipValueLabel}
    />
  )

  const pieChartInner = (
    <Box position="relative" width="100%" height="100%">
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart>
          <Pie
            data={dataWithColors}
            dataKey={valueKey}
            nameKey={nameKey}
            cx="50%"
            cy="50%"
            innerRadius={PIE_INNER_RADIUS}
            outerRadius={PIE_OUTER_RADIUS}
            stroke="rgba(255, 255, 255, 0.1)"
            strokeWidth={2}
            isAnimationActive={false}
            label={renderPercentLabel}
            labelLine={false}
          >
            {dataWithColors.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip content={<FloatingPieTooltipWrapper />} />
        </RechartsPieChart>
      </ResponsiveContainer>
      <VStack
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        spacing="3px"
        pointerEvents="none"
      >
        <Text color="white" fontSize="28px" fontWeight="600" lineHeight="1">
          {formatChartDataLabelValue(total, chartData.valueFormat)}
        </Text>
        <Text color="white" fontSize="12px" fontWeight="500" lineHeight="1">
          {centerTotalCaption}
        </Text>
      </VStack>
    </Box>
  )

  return (
    <Box {...props}>
      <Flex
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
        columnGap={`clamp(${PIE_CHART_MIN_LEGEND_GAP}px, 4vw, ${PIE_CHART_MAX_LEGEND_GAP}px)`}
        rowGap="24px"
      >
        <FloatingChartTooltipShell>
          {mouseHandlers => (
            <Box
              flexShrink={0}
              w={`${PIE_PLOT_WIDTH_PX}px`}
              maxW="100%"
              height={chartHeight}
              {...mouseHandlers}
            >
              {pieChartInner}
            </Box>
          )}
        </FloatingChartTooltipShell>

        <Box
          maxHeight={chartHeight}
          overflowY="auto"
          flexShrink={0}
          minW="min-content"
          pr="4px"
        >
          <VStack alignItems="flex-start" spacing="11px">
            {dataWithColors.map((entry, index) => {
              const rawName = entry[nameKey]
              const name = String(rawName ?? '')
              return (
                <HStack key={index} spacing="8px" alignItems="center">
                  <Box
                    w="19px"
                    h="19px"
                    borderRadius="full"
                    bg={entry.fill}
                    boxShadow="0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)"
                    flexShrink={0}
                  />
                  <Text color="white" fontSize="12px" fontWeight="500">
                    {name}
                  </Text>
                </HStack>
              )
            })}
          </VStack>
        </Box>
      </Flex>
    </Box>
  )
}
